import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { getProjectRepository } from "../../data/projectRepository";
import type { CourseNature } from "../../storage/resourceRoomDocument";
import { ResourceRoomDialog } from "../shared/ResourceRoomDialog";
import { ResourceRoomProjectHeader } from "../shared/ResourceRoomProjectHeader";
import { demoCourseNature } from "../shared/resourceRoomDemoData";
import "../shared/resource-room-workspace.css";

const natureLabels: Record<CourseNature, string> = {
  PREFERRED: "优先抽离",
  GENERAL: "一般",
  PROTECTED: "保护课程"
};

interface Feedback {
  kind: "success" | "error";
  text: string;
}

export function CourseNaturePage({
  projectId,
  embedded = false
}: {
  projectId: string;
  embedded?: boolean;
}): React.JSX.Element {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string[]>([]);
  const [nature, setNature] = useState<CourseNature>("PREFERRED");
  const [filter, setFilter] = useState<"ALL" | "UNSET">("UNSET");
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [editingCourseId, setEditingCourseId] = useState<string | null>(null);
  const [editingNature, setEditingNature] = useState<CourseNature>("PREFERRED");
  const documentQuery = useQuery({
    queryKey: ["resource-room-document", projectId],
    queryFn: async () => (await getProjectRepository()).readDocument(projectId)
  });
  const mutation = useMutation({
    mutationFn: async ({ courseIds, natureValue }: { courseIds: string[]; natureValue: CourseNature }) =>
      (await getProjectRepository()).execute(projectId, "SET_COURSE_NATURE", {
        course_ids: courseIds,
        nature: natureValue
      }),
    onSuccess: async (_, variables) => {
      setSelected([]);
      setEditingCourseId(null);
      setFeedback({
        kind: "success",
        text: `${variables.courseIds.length === 1 ? "当前课程" : `${variables.courseIds.length} 门课程`}性质已保存；已有安排已按新性质重新检查。`
      });
      await queryClient.invalidateQueries({ predicate: (item) => item.queryKey.includes(projectId) });
    },
    onError: (error: unknown) => setFeedback({ kind: "error", text: error instanceof Error ? error.message : "保存失败" })
  });
  const demoMutation = useMutation({
    mutationFn: async () => {
      const repository = await getProjectRepository();
      const current = await repository.readDocument(projectId);
      const grouped: Record<CourseNature, string[]> = {
        PREFERRED: [],
        GENERAL: [],
        PROTECTED: []
      };
      for (const course of current.entities.original_courses) {
        if (!course.nature) grouped[demoCourseNature(course.name)].push(course.id);
      }
      let updatedCount = 0;
      for (const natureValue of ["PREFERRED", "GENERAL", "PROTECTED"] as const) {
        const courseIds = grouped[natureValue];
        if (!courseIds.length) continue;
        await repository.execute(projectId, "SET_COURSE_NATURE", {
          course_ids: courseIds,
          nature: natureValue
        });
        updatedCount += courseIds.length;
      }
      return updatedCount;
    },
    onSuccess: async (updatedCount) => {
      setSelected([]);
      setFeedback({
        kind: "success",
        text: `已为 ${updatedCount} 门未设置课程应用模拟性质：主科保护，艺体与综合课程优先，其余一般。`
      });
      await queryClient.invalidateQueries({ predicate: (item) => item.queryKey.includes(projectId) });
    },
    onError: (error: unknown) => setFeedback({
      kind: "error",
      text: error instanceof Error ? error.message : "模拟课程性质填写失败"
    })
  });
  const courses = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase();
    return (documentQuery.data?.entities.original_courses ?? []).filter((course) =>
      (filter === "ALL" || course.nature === null) &&
      (!normalized || course.name.toLocaleLowerCase().includes(normalized))
    );
  }, [documentQuery.data, filter, query]);
  if (!documentQuery.data) {
    return embedded
      ? <p className="state-message">正在读取课程性质…</p>
      : <main className="page-shell"><p className="state-message">正在读取课程性质…</p></main>;
  }
  const allCourses = documentQuery.data.entities.original_courses;
  const editingCourse = allCourses.find((course) => course.id === editingCourseId);
  const unsetCount = allCourses.filter((item) => item.nature === null).length;
  const busy = mutation.isPending || demoMutation.isPending;
  return (
    <div className={embedded ? undefined : "page-shell"}>
      {!embedded ? (
        <ResourceRoomProjectHeader
          projectId={projectId}
          eyebrow="阶段 1 · 学校基础数据"
          title="全校原班课程性质"
          actions={(
            <button
              className="secondary-button demo-data-button"
              type="button"
              disabled={!unsetCount || busy}
              onClick={() => demoMutation.mutate()}
            >
              {demoMutation.isPending
                ? "正在应用…"
                : unsetCount
                  ? `应用模拟课程性质（${unsetCount} 项）`
                  : "课程性质已全部设置"}
            </button>
          )}
        />
      ) : null}
      <section className="resource-panel">
        {embedded ? (
          <div className="resource-section-heading">
            <div>
              <h2>全校原班课程性质</h2>
              <p>相同课程名称在当前项目内统一设置为优先抽离、一般或保护课程。</p>
            </div>
            <button
              className="secondary-button demo-data-button"
              type="button"
              disabled={!unsetCount || busy}
              onClick={() => demoMutation.mutate()}
            >
              {demoMutation.isPending
                ? "正在应用…"
                : unsetCount
                  ? `应用模拟课程性质（${unsetCount} 项）`
                  : "课程性质已全部设置"}
            </button>
          </div>
        ) : null}
        <div className="resource-summary-grid">
          <div className="resource-summary-card"><strong>{allCourses.length}</strong><span>已识别课程</span></div>
          <div className="resource-summary-card"><strong>{unsetCount}</strong><span>待设置</span></div>
          <div className="resource-summary-card"><strong>{allCourses.length - unsetCount}</strong><span>已完成</span></div>
        </div>
        {unsetCount ? <div className="resource-errors">还有 {unsetCount} 门课程未设置。</div> : null}
        <div className="resource-batch-bar">
          <button className={filter === "UNSET" ? "primary-button" : "secondary-button"} type="button" onClick={() => setFilter("UNSET")}>只看未设置</button>
          <button className={filter === "ALL" ? "primary-button" : "secondary-button"} type="button" onClick={() => setFilter("ALL")}>查看全部</button>
          <label className="field"><span>搜索课程</span><input value={query} onChange={(event) => setQuery(event.target.value)} /></label>
          <label className="field"><span>批量设为</span><select value={nature} onChange={(event) => setNature(event.target.value as CourseNature)}>{Object.entries(natureLabels).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select></label>
          <button className="primary-button" type="button" disabled={!selected.length || busy} onClick={() => mutation.mutate({ courseIds: selected, natureValue: nature })}>保存所选 {selected.length || ""} 项</button>
        </div>
        {feedback ? <p className={feedback.kind === "success" ? "resource-success" : "resource-errors"} role={feedback.kind === "error" ? "alert" : "status"}>{feedback.text}</p> : null}
        <div className="resource-table-scroll">
          <table className="data-table">
            <thead><tr><th><input aria-label="选择当前全部课程" type="checkbox" checked={courses.length > 0 && courses.every((item) => selected.includes(item.id))} onChange={(event) => setSelected(event.target.checked ? Array.from(new Set([...selected, ...courses.map((item) => item.id)])) : selected.filter((id) => !courses.some((item) => item.id === id)))} /></th><th>课程名称</th><th>当前性质</th><th>操作</th></tr></thead>
            <tbody>{courses.map((course) => <tr key={course.id}><td><input aria-label={`选择 ${course.name}`} type="checkbox" checked={selected.includes(course.id)} onChange={(event) => setSelected(event.target.checked ? [...selected, course.id] : selected.filter((id) => id !== course.id))} /></td><td><strong>{course.name}</strong></td><td>{course.nature ? natureLabels[course.nature] : <span className="resource-status pending">未设置</span>}</td><td><button className="text-button" type="button" onClick={() => { setEditingCourseId(course.id); setEditingNature(course.nature ?? "PREFERRED"); }}>设置性质</button></td></tr>)}</tbody>
          </table>
        </div>
      </section>
      {editingCourse ? (
        <ResourceRoomDialog
          title={`设置“${editingCourse.name}”的课程性质`}
          description="同名课程在当前学期项目中统一使用这一性质。"
          busy={mutation.isPending}
          onClose={() => setEditingCourseId(null)}
        >
          <fieldset className="resource-nature-options">
            <legend>选择当前性质</legend>
            {([
              ["PREFERRED", "优先抽离", "候选计算优先选择这类原班课程时段。"],
              ["GENERAL", "一般", "可以抽离，但排序优先级低于优先抽离课程。"],
              ["PROTECTED", "保护课程", "发生抽离时必须在保存方案前明确确认。"]
            ] as const).map(([value, label, description]) => (
              <label key={value}>
                <input type="radio" name="course_nature" value={value} checked={editingNature === value} onChange={() => setEditingNature(value)} />
                <span><strong>{label}</strong><small>{description}</small></span>
              </label>
            ))}
          </fieldset>
          <div className="modal-actions">
            <button className="secondary-button" type="button" disabled={mutation.isPending} onClick={() => setEditingCourseId(null)}>取消</button>
            <button className="primary-button" type="button" disabled={mutation.isPending} onClick={() => mutation.mutate({ courseIds: [editingCourse.id], natureValue: editingNature })}>{mutation.isPending ? "正在保存…" : "保存当前课程"}</button>
          </div>
        </ResourceRoomDialog>
      ) : null}
    </div>
  );
}
